import { useCallback, useState } from 'react';
import { Upload, FileText, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useUploadCv, useApplyCv } from '@/hooks/use-profile';
import { CvReviewDialog, type ExtractedCvData } from './cv-review-dialog';
import { useT } from '@/lib/i18n';

type UploadMode = 'upload' | 'paste';

export function CvUpload() {
  const upload = useUploadCv();
  const applyCv = useApplyCv();
  const tFn = useT();
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState<UploadMode>('upload');
  const [pastedText, setPastedText] = useState('');
  const [extractedData, setExtractedData] = useState<ExtractedCvData | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  const handleFile = useCallback(
    (file: File) => {
      const name = file.name.toLowerCase();
      if (!name.endsWith('.pdf') && !name.endsWith('.docx') && !name.endsWith('.doc')) {
        alert('Only PDF or Word (.docx, .doc) files are supported');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        upload.mutate(
          { fileBuffer: base64, fileName: file.name },
          {
            onSuccess: (data) => {
              if (data?.data?.extracted) {
                setExtractedData(data.data.extracted as unknown as ExtractedCvData);
                setReviewOpen(true);
              }
            }
          }
        );
      };
      reader.readAsDataURL(file);
    },
    [upload]
  );

  const handleMerge = useCallback(() => {
    if (!extractedData) return;
    applyCv.mutate(extractedData as unknown as Record<string, unknown>, {
      onSuccess: () => {
        setExtractedData(null);
        setReviewOpen(false);
      }
    });
  }, [applyCv, extractedData]);

  const handleCancelReview = useCallback(() => {
    setExtractedData(null);
    setReviewOpen(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handlePasteSubmit = useCallback(() => {
    if (!pastedText.trim()) return;
    const base64 = btoa(unescape(encodeURIComponent(pastedText)));
    upload.mutate(
      { fileBuffer: base64, fileName: 'pasted-cv.txt' },
      {
        onSuccess: (data) => {
          if (data?.data?.extracted) {
            setExtractedData(data.data.extracted as unknown as ExtractedCvData);
            setReviewOpen(true);
          }
        }
      }
    );
  }, [pastedText, upload]);

  const renderUploadZone = () => {
    if (upload.isPending) {
      return (
        <>
          <Loader2 className="mb-2 h-8 w-8 animate-spin text-primary" />
          <p className="text-sm font-medium">{tFn('profile.parsing')}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            AI is extracting skills and experience
          </p>
        </>
      );
    }

    if (upload.isSuccess && !extractedData) {
      return (
        <>
          <FileText className="mb-2 h-8 w-8 text-green-500 dark:text-green-400" />
          <p className="text-sm font-medium text-green-600 dark:text-green-400">
            {tFn('profile.uploaded')}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Your profile is being enriched. Refresh in a few seconds.
          </p>
        </>
      );
    }

    return (
      <>
        <Upload className="mb-2 h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">{tFn('profile.dropHere')}</p>
        <p className="mt-1 text-xs text-muted-foreground">{tFn('profile.pdfOrWord')}</p>
        <label className="mt-3">
          <input
            type="file"
            accept=".pdf,.docx,.doc"
            onChange={handleFileInput}
            className="hidden"
          />
          <Button size="sm" variant="outline" asChild>
            <span>Choose File</span>
          </Button>
        </label>
      </>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Upload className="h-4 w-4" />
          {tFn('profile.uploadCv')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Mode tabs */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setMode('upload')}
            className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
              mode === 'upload'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            {tFn('profile.uploadFile')}
          </button>
          <button
            onClick={() => setMode('paste')}
            className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
              mode === 'paste'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            {tFn('profile.pasteText')}
          </button>
        </div>

        {/* Upload file mode */}
        {mode === 'upload' && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-muted-foreground/50'
            }`}
          >
            {renderUploadZone()}
          </div>
        )}

        {/* Paste text mode */}
        {mode === 'paste' && (
          <div className="space-y-3">
            <textarea
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              placeholder={tFn('profile.pastePlaceholder')}
              rows={8}
              className="w-full rounded-md border bg-background p-3 text-sm outline-none focus:ring-1 focus:ring-ring resize-y"
            />
            <Button
              size="sm"
              onClick={handlePasteSubmit}
              disabled={upload.isPending || !pastedText.trim()}
            >
              {upload.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  {tFn('profile.parsing')}
                </>
              ) : (
                tFn('profile.parseCvText')
              )}
            </Button>
          </div>
        )}

        {upload.isError && (
          <p className="mt-2 text-xs text-red-500 dark:text-red-400">
            Upload failed. Please try again.
          </p>
        )}
      </CardContent>

      <CvReviewDialog
        extracted={extractedData}
        open={reviewOpen}
        onOpenChange={(open) => {
          if (!open) handleCancelReview();
        }}
        onMerge={handleMerge}
      />
    </Card>
  );
}
